
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthSession } from '@/hooks/use-auth-session';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface UsePlaylistCardProps {
  id: string;
  name: string;
  description?: string | null;
}

export const usePlaylistCard = ({ id, name, description }: UsePlaylistCardProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const session = useAuthSession();
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddPlaceDialogOpen, setIsAddPlaceDialogOpen] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const [editedDescription, setEditedDescription] = useState(description || '');
  const [isPublic, setIsPublic] = useState(true);
  
  const { data: isAdmin } = useQuery({
    queryKey: ['is-admin', session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('is_admin', { user_id: session?.user?.id });
      
      if (error) throw error;
      return !!data;
    }
  });

  const { data: playlistData } = useQuery({
    queryKey: ['playlist-detail', id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('destination_playlists')
        .select('user_id, is_public')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  const isOwner = session?.user?.id === playlistData?.user_id;
  const canEdit = isOwner || isAdmin;
  const isAuthenticated = !!session?.user?.id;

  const { data: isFollowing } = useQuery({
    queryKey: ['collection-follow', id, session?.user?.id],
    enabled: !!session?.user?.id && !isOwner,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collection_follows')
        .select('id')
        .eq('collection_id', id)
        .eq('user_id', session.user.id)
        .maybeSingle();
      
      if (error) throw error;
      return !!data;
    }
  });

  const { data: followersCount } = useQuery({
    queryKey: ['collection-followers', id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('collection_follows')
        .select('*', { count: 'exact', head: true })
        .eq('collection_id', id);
      
      if (error) throw error;
      return count || 0;
    }
  });

  const { data: places } = useQuery({
    queryKey: ['playlist-items', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('destination_playlist_items')
        .select('*')
        .eq('playlist_id', id);
      
      if (error) throw error;
      return data;
    }
  });

  const followMutation = useMutation({
    mutationFn: async () => {
      if (!session?.user?.id) {
        throw new Error('Must be logged in to follow collections');
      }

      if (isFollowing) {
        const { error } = await supabase
          .from('collection_follows')
          .delete()
          .eq('collection_id', id)
          .eq('user_id', session.user.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('collection_follows')
          .insert([{
            collection_id: id,
            user_id: session.user.id
          }]);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-follow', id] });
      queryClient.invalidateQueries({ queryKey: ['collection-followers', id] });
      toast({
        title: "Success",
        description: `Successfully ${isFollowing ? 'unfollowed' : 'followed'} collection`,
      });
    },
    onError: (error: Error) => {
      if (error.message === 'Must be logged in to follow collections') {
        navigate('/auth');
        toast({
          title: "Authentication Required",
          description: "Please sign in to follow collections",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  });

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('destination_playlists')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Collection deleted successfully",
      });

      queryClient.invalidateQueries({ queryKey: ['playlists'] });
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast({
        title: "Error",
        description: "Failed to delete collection",
        variant: "destructive",
      });
    }
  };

  const handleUpdate = async () => {
    try {
      const { error } = await supabase
        .from('destination_playlists')
        .update({
          name: editedName,
          description: editedDescription,
          is_public: isPublic,
        })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Collection updated successfully",
      });

      queryClient.invalidateQueries({ queryKey: ['playlists'] });
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating collection:', error);
      toast({
        title: "Error",
        description: "Failed to update collection",
        variant: "destructive",
      });
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button') || 
        (e.target as HTMLElement).closest('[role="dialog"]')) {
      return;
    }
    
    const nameSlug = name.toLowerCase().replace(/\s+/g, '-');
    navigate(`/collections/${nameSlug}/${id}`);
  };

  const handleAddPlaceClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAddPlaceDialogOpen(true);
  };

  return {
    isEditDialogOpen,
    setIsEditDialogOpen,
    isAddPlaceDialogOpen,
    setIsAddPlaceDialogOpen,
    editedName,
    setEditedName,
    editedDescription,
    setEditedDescription,
    isPublic,
    setIsPublic,
    isAdmin,
    playlistData,
    isOwner,
    canEdit,
    isAuthenticated,
    isFollowing,
    followersCount,
    places,
    followMutation,
    handleDelete,
    handleUpdate,
    handleCardClick,
    handleAddPlaceClick,
  };
};
